import { type Location, routes } from "@src/appV2/redesign/utils/routes";

export type SessionRole = "admin" | "worker" | "workplace";

/**
 * Discriminated by `role`: admin and worker have static homes and ignore
 * workplace flags entirely; workplace requires the four flags that govern
 * its landing page (Home Health → Schedule → Facility dashboard fallback).
 *
 * Home Health workplaces always land on Cases regardless of viewport.
 * The previous `!isMobileViewport` gate fell HH-on-mobile through to
 * `/schedule`, which `dailyView` privilege denies for HH non-admins
 * (HOME_HEALTH_MENU_ITEMS_HIDDEN), causing an infinite redirect loop via
 * `PrivateRoute` → `/enter` → `renderStartPage` (TECH-1370). The matching
 * `/facility/dashboard` redirect in `AllRoutes.tsx` was updated alongside
 * this for the same reason — without that, the loop is still reachable
 * via the legacy facility-dashboard URL.
 *
 * `isMobileViewport` is the screen-width breakpoint (`useIsMobile()` from
 * the redesign), NOT `Platform.isMobile` (Capacitor native check). It is
 * retained on the params type because callers still pass it; remove once
 * call sites are updated.
 */
export type GetHomeRouteForSessionParams =
  | { role: "admin" }
  | { role: "worker" }
  | {
      role: "workplace";
      isMobileViewport: boolean;
      isHomeHealthEnabled: boolean;
      isScheduleTabEnabled: boolean;
      isMonthlyViewEnabled: boolean;
    };

/**
 * Resolves the landing route for a given session shape. Single source of
 * truth for "where does this user type belong when no other URL is in
 * play" — used by `renderStartPage` and by `useSwitchUser` so that
 * context switches navigate directly to the new role's home instead of
 * bouncing through `/enter` against stale Redux state (ORB-1847).
 */
export function getHomeRouteForSession(params: GetHomeRouteForSessionParams): Location {
  if (params.role === "admin") {
    return { pathname: routes.adminAccountsAgents };
  }

  if (params.role === "worker") {
    return { pathname: routes.agentDashboard };
  }

  const { isHomeHealthEnabled, isScheduleTabEnabled } = params;

  if (isHomeHealthEnabled) {
    return { pathname: routes.homeHealthCases };
  }

  if (isScheduleTabEnabled) {
    return { pathname: routes.schedule };
  }

  return { pathname: routes.facilityDashboard };
}

export interface SessionRoleFlags {
  isAdmin: boolean;
  isWorker: boolean;
  isWorkplace: boolean;
}

/**
 * Resolves a `SessionRole` from the boolean flags computed in
 * `mapStateToProps` (admin, worker, workplace). Used by `renderStartPage`
 * — for post-`pseudo()` derivation from a live Redux session, see
 * `useSwitchUser` instead.
 */
export function getSessionRoleFromFlags(flags: SessionRoleFlags): SessionRole | undefined {
  const { isAdmin, isWorker, isWorkplace } = flags;

  if (isAdmin) {
    return "admin";
  }
  if (isWorker) {
    return "worker";
  }
  if (isWorkplace) {
    return "workplace";
  }
  return undefined;
}
